<template>
  <!-- Acabamento do SelectCard (borda primary30, sombra só no hover), com a
       contagem e o rodapé que o SelectCard não tem. Card inteiro é o link.
       `div role="button"` como o SelectCard: o preflight do Tailwind está
       desligado e o reset global de <button> zera a borda. -->
  <div
    role="button"
    tabindex="0"
    class="flex flex-col gap-2 p-4 h-full w-full border border-primary30 rounded-lg cursor-pointer bg-white hover:shadow-md transition-shadow text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary30"
    :data-testid="testId"
    @click="emit('click')"
    @keydown.enter="emit('click')"
    @keydown.space.prevent="emit('click')"
  >
    <div class="flex justify-between items-start w-full">
      <div class="bg-primary30 rounded-[11.2px] p-2.5">
        <svg viewBox="0 0 24 24" fill="white" class="w-6 h-6" aria-hidden="true">
          <path :d="icon" />
        </svg>
      </div>
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        class="w-6 h-6 text-primary30"
        aria-hidden="true"
      >
        <path :d="mdiChevronRight" />
      </svg>
    </div>

    <div class="flex flex-col flex-1">
      <span
        class="flex items-center gap-2 text-primary50 font-bold text-base leading-6 tracking-[0.08px]"
      >
        {{ title }}
        <LabelBadge v-if="badge" :label="badge" color="blue" size="small" :icon="null" />
      </span>
      <span class="text-[#52526F] text-sm leading-[18px] tracking-[0.07px]">
        {{ description }}
      </span>

      <!-- Métrica: última linha do corpo, agrupada com o rodapé (não com a descrição). -->
      <div class="mt-3 flex items-baseline gap-2 min-h-[32px]" :data-testid="`${testId}-count`">
        <template v-if="isLoading">
          <span class="h-7 w-10 rounded bg-slate-200 animate-pulse" aria-hidden="true" />
          <span class="sr-only">Carregando contagem</span>
        </template>
        <template v-else>
          <strong
            class="text-2xl leading-8 font-bold tabular-nums"
            :class="count ? 'text-primary50' : 'text-neutral50'"
          >
            {{ count ?? DISPLAY_FALLBACK }}
          </strong>
          <span class="text-sm leading-[18px] text-[#52526F]">{{ countLabelText }}</span>
        </template>
      </div>
    </div>

    <!-- Rodapé colado embaixo (o corpo tem flex-1): idade da fila / última decisão. -->
    <span
      v-if="footerText"
      class="flex items-center gap-1 text-sm leading-[18px]"
      :class="footerVariant === 'alert' ? 'text-alert font-medium' : 'text-[#52526F]'"
      :data-testid="`${testId}-footer`"
    >
      <svg
        v-if="footerIcon"
        viewBox="0 0 24 24"
        fill="currentColor"
        class="w-4 h-4 shrink-0"
        aria-hidden="true"
      >
        <path :d="footerIcon" />
      </svg>
      {{ footerText }}
    </span>

    <span
      v-if="!isLoading && errorMessage"
      class="flex items-center gap-1 text-xs text-neutral50"
      :data-testid="`${testId}-error`"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 shrink-0" aria-hidden="true">
        <path :d="mdiAlertCircleOutline" />
      </svg>
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiAlertCircleOutline, mdiChevronRight } from '@mdi/js'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'

const DISPLAY_FALLBACK = '—'

const props = withDefaults(
  defineProps<{
    icon: string
    title: string
    description: string
    /** Rótulo da contagem ("aguardando aprovação"); vira "nada aguardando" no zero. */
    countLabel: string
    /** `null` = contagem indisponível (erro) — mostra "—". */
    count: number | null
    isLoading?: boolean
    errorMessage?: string | null
    badge?: string | null
    footerText?: string | null
    footerIcon?: string | null
    footerVariant?: 'default' | 'alert'
    testId: string
  }>(),
  {
    isLoading: false,
    errorMessage: null,
    badge: null,
    footerText: null,
    footerIcon: null,
    footerVariant: 'default'
  }
)

const emit = defineEmits<{ click: [] }>()

const countLabelText = computed(() => {
  if (props.count === null) return 'contagem indisponível'
  return props.count === 0 ? 'nada aguardando' : props.countLabel
})
</script>
